import { useCallback, useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Container, DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { HarborKYCData } from '@/types/harbor-management.types';
import { PaginatedApiResponse } from '@/types/api.types';
import { useGetKYCListQuery } from '@/services/KYCService';
import { KYCStatus, KYCType } from '@/constant';

type KYCData = Omit<HarborKYCData, 'entity_type'> & {
  entity_type: 'harbor' | 'club' | 'service_provider';
};

const KYCDetailsTab = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<
    | KYCStatus.PENDING
    | KYCStatus.UNDER_REVIEW
    | KYCStatus.APPROVED
    | KYCStatus.REJECTED
    | KYCStatus.REQUIRES_UPDATE
    | KYCStatus.NOT_REQUIRED
    | KYCStatus.ON_HOLD
    | KYCStatus.RESUBMITTED
    | 'all'
  >('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState<
    KYCType.HARBOR | KYCType.CLUB | KYCType.SERVICE_PROVIDER | 'all'
  >('all');

  const {
    pagination,
    setPagination,
    handleSearch,
    searchTerm,
    resetSearch,
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
  } = usePagination();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue || undefined,
      kycStatus:
        statusFilter !== 'all'
          ? (statusFilter as
              | 'pending'
              | 'under_review'
              | 'approved'
              | 'rejected'
              | 'requires_update'
              | 'not_required'
              | 'on_hold'
              | 'resubmitted')
          : undefined,
      entityType: entityTypeFilter !== 'all' ? entityTypeFilter : undefined,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      debouncedValue,
      statusFilter,
      entityTypeFilter,
    ]
  );

  const {
    data: kycResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetKYCListQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  }) as {
    data: PaginatedApiResponse<KYCData[]> | KYCData[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const filteredData = useMemo(() => {
    // Handle both cases: array directly or wrapped in PaginatedApiResponse
    if (Array.isArray(kycResponse)) {
      return kycResponse as unknown as KYCData[];
    }
    const apiData = (kycResponse?.data ?? []) as unknown as KYCData[];
    // Use API data as-is without transformation
    return apiData;
  }, [kycResponse]);

  useEffect(() => {
    if (kycResponse) {
      // Handle both cases: array directly or wrapped in PaginatedApiResponse
      if (Array.isArray(kycResponse)) {
        // If it's an array, set defaults for pagination
        handleTotalPages(1);
        handleTotalItems(kycResponse.length);
      } else {
        const totalPagesFromApi =
          kycResponse.total_pages ?? kycResponse.totalPages ?? 1;
        const totalItemsFromApi =
          kycResponse.total ??
          kycResponse.totalItems ??
          kycResponse.total_items ??
          0;

        handleTotalPages(totalPagesFromApi || 1);
        handleTotalItems(totalItemsFromApi || 0);
      }
    }
  }, [kycResponse, handleTotalPages, handleTotalItems]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case KYCStatus.PENDING:
        return <span className="badge badge-info">Pending</span>;
      case KYCStatus.UNDER_REVIEW:
        return <span className="badge badge-warning">Under Review</span>;
      case KYCStatus.APPROVED:
        return <span className="badge badge-success">Approved</span>;
      case KYCStatus.REJECTED:
        return <span className="badge badge-danger">Rejected</span>;
      case KYCStatus.REQUIRES_UPDATE:
        return <span className="badge badge-warning">Requires Update</span>;
      case KYCStatus.NOT_REQUIRED:
        return <span className="badge badge-secondary">Not Required</span>;
      case KYCStatus.ON_HOLD:
        return <span className="badge badge-warning">On Hold</span>;
      case KYCStatus.RESUBMITTED:
        return <span className="badge badge-info">Resubmitted</span>;
      case KYCStatus.DRAFT:
        return <span className="badge badge-secondary">Not Submitted</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  const getEntityTypeLabel = (entityType: string) => {
    switch (entityType) {
      case KYCType.HARBOR:
        return 'Harbor';
      case KYCType.CLUB:
        return 'Club';
      case KYCType.SERVICE_PROVIDER:
        return 'Service Provider';
      default:
        return entityType;
    }
  };

  const handleViewKYC = useCallback(
    (kyc: KYCData) => {
      navigate(`/kyc-management/details?kycId=${kyc.id}&mode=view`);
    },
    [navigate]
  );

  const handleEditKYC = useCallback(
    (kyc: KYCData) => {
      navigate(`/kyc-management/details?kycId=${kyc.id}&mode=edit`);
    },
    [navigate]
  );

  const columns = useMemo<ColumnDef<KYCData>[]>(
    () => [
      {
        accessorKey: 'legal_name',
        header: 'Legal Name',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => handleViewKYC(row.original)}
            className="block text-left hover:text-primary hover:underline cursor-pointer"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.legal_name}
            aria-label={`View ${row.original.legal_name} KYC details`}
          >
            {row.original.legal_name}
          </button>
        ),
      },
      {
        accessorKey: 'entity_type',
        header: 'Entity Type',
        cell: ({ row }) => (
          <span className="block">
            {getEntityTypeLabel(row.original.entity_type)}
          </span>
        ),
      },
      {
        accessorKey: 'business_id',
        header: 'Business ID',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.business_id}
          >
            {row.original.business_id}
          </span>
        ),
      },
      {
        accessorKey: 'vat_id',
        header: 'VAT ID',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.vat_id}
          >
            {row.original.vat_id}
          </span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.city}
          >
            {row.original.city}
          </span>
        ),
      },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.country}
          >
            {row.original.country}
          </span>
        ),
      },
      {
        accessorKey: 'kyc_status',
        header: 'Status',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {getStatusBadge(row.original.kyc_status)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              onClick={() => handleViewKYC(row.original)}
              title="View Details"
              aria-label={`View ${row.original.legal_name} details`}
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() => handleEditKYC(row.original)}
              title="Edit"
              aria-label={`Edit ${row.original.legal_name}`}
            >
              <KeenIcon icon="pencil" className="text-base" />
            </button>
          </div>
        ),
      },
    ],
    [handleViewKYC, handleEditKYC]
  );

  return (
    <Container>
      <div
        className="card card-grid"
        style={{ minWidth: 0, width: '100%', overflowX: 'auto' }}
      >
        <div
          className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-start"
          style={{ minWidth: 0 }}
        >
          <div
            className="flex items-center gap-2"
            style={{
              minWidth: 0,
              flexWrap: 'nowrap',
            }}
          >
            <label
              htmlFor="search-kyc"
              className="input input-sm h-8 sm:h-10 flex items-center"
              style={{ minWidth: '180px', width: '240px', flexShrink: 1 }}
            >
              <KeenIcon
                icon="magnifier"
                className="text-gray-500 flex-shrink-0"
                aria-hidden="true"
              />
              <input
                id="search-kyc"
                placeholder="Search KYC records"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search KYC records by name, business ID, or VAT ID"
                style={{ minWidth: 0 }}
              />
            </label>
            <select
              id="entity-type-filter"
              className="select select-sm h-8 sm:h-10 text-sm"
              value={entityTypeFilter}
              onChange={(e) =>
                setEntityTypeFilter(
                  e.target.value as
                    | KYCType.HARBOR
                    | KYCType.CLUB
                    | KYCType.SERVICE_PROVIDER
                    | 'all'
                )
              }
              aria-label="Filter KYC by entity type"
              style={{ minWidth: '130px', width: '150px', flexShrink: 0 }}
            >
              <option value="all">All Entity Types</option>
              <option value={KYCType.HARBOR}>Harbor</option>
              <option value={KYCType.CLUB}>Club</option>
              <option value={KYCType.SERVICE_PROVIDER}>Service Provider</option>
            </select>
            <select
              id="status-filter"
              className="select select-sm h-8 sm:h-10 text-sm"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as
                    | KYCStatus.PENDING
                    | KYCStatus.UNDER_REVIEW
                    | KYCStatus.APPROVED
                    | KYCStatus.REJECTED
                    | KYCStatus.REQUIRES_UPDATE
                    | KYCStatus.NOT_REQUIRED
                    | KYCStatus.ON_HOLD
                    | KYCStatus.RESUBMITTED
                    | 'all'
                )
              }
              aria-label="Filter KYC by status"
              style={{ minWidth: '130px', width: '150px', flexShrink: 0 }}
            >
              <option value="all">All Status</option>
              <option value={KYCStatus.PENDING}>Pending</option>
              <option value={KYCStatus.UNDER_REVIEW}>Under Review</option>
              <option value={KYCStatus.APPROVED}>Approved</option>
              <option value={KYCStatus.REJECTED}>Rejected</option>
              <option value={KYCStatus.NOT_REQUIRED}>Not Required</option>
              <option value={KYCStatus.REQUIRES_UPDATE}>Requires Update</option>
              <option value={KYCStatus.ON_HOLD}>On Hold</option>
              <option value={KYCStatus.RESUBMITTED}>Resubmitted</option>
            </select>
          </div>
          {(searchTerm ||
            statusFilter !== 'all' ||
            entityTypeFilter !== 'all') && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 whitespace-nowrap"
              onClick={() => {
                resetSearch();
                setStatusFilter('all');
                setEntityTypeFilter('all');
              }}
              aria-label="Clear all search and filters"
              style={{ flexShrink: 0 }}
            >
              <KeenIcon
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              <span>Clear Filters</span>
            </button>
          )}
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4">Loading KYC records...</div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500">
              Failed to load KYC records. Please try again.
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: pagination.pageIndex + 1 < totalPages,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'legal_name', desc: false }]}
              serverSide={true}
              onFilterReset={() => {
                resetSearch();
                setStatusFilter('all');
                setEntityTypeFilter('all');
              }}
              hasActiveFilters={
                !!searchTerm ||
                statusFilter !== 'all' ||
                entityTypeFilter !== 'all'
              }
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default KYCDetailsTab;
